import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Download, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import safeLogo from "@/assets/Safe_logo.webp";
import type { AppVersionConfig } from "@/hooks/useAppVersionGate";

interface Props {
  currentVersion: string | null;
  config: AppVersionConfig;
}

export default function MandatoryUpdateScreen({ currentVersion, config }: Props) {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let handle: { remove: () => void } | null = null;
    App.addListener("backButton", () => {
      // Swallow — prevent back navigation past the update screen.
    }).then((h) => {
      handle = h;
    });
    return () => {
      handle?.remove();
    };
  }, []);

  const openStore = () => {
    const pkg = config.package_name;
    const fallback = config.play_store_url;
    let opened = false;
    const onBlur = () => {
      opened = true;
    };
    window.addEventListener("blur", onBlur, { once: true });
    try {
      window.location.href = `market://details?id=${pkg}`;
    } catch {
      /* ignore */
    }
    setTimeout(() => {
      window.removeEventListener("blur", onBlur);
      if (!opened) {
        window.open(fallback, "_system" as any) || (window.location.href = fallback);
      }
    }, 600);
  };

  const title = config.update_title || "Update Required";
  const message =
    config.update_message ||
    "A newer version of the app is available. Please update to continue.";

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-between px-6 py-10 text-white"
      style={{
        background:
          "linear-gradient(160deg, hsl(211 95% 45%) 0%, hsl(211 90% 38%) 60%, hsl(211 85% 30%) 100%)",
        paddingTop: "calc(env(safe-area-inset-top, 0px) + 2.5rem)",
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 2.5rem)",
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="update-required-title"
    >
      <div className="flex flex-1 flex-col items-center justify-center text-center max-w-sm">
        <div className="mb-8 flex h-28 w-28 items-center justify-center rounded-3xl bg-white/15 shadow-2xl backdrop-blur-sm">
          <img src={safeLogo} alt="App icon" className="h-20 w-20 object-contain" />
        </div>

        <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-1.5 text-xs font-medium uppercase tracking-wider">
          <AlertTriangle className="h-3.5 w-3.5" />
          Action Needed
        </div>

        <h1 id="update-required-title" className="mb-4 text-3xl font-bold leading-tight">
          {title}
        </h1>

        <p className="mb-8 text-base leading-relaxed text-white/90">{message}</p>

        <div className="flex w-full items-center justify-center gap-2 text-sm">
          <span className="rounded-md bg-white/10 px-3 py-1.5">
            Installed&nbsp;
            <span className="font-semibold">{currentVersion ?? "—"}</span>
          </span>
          <span className="text-white/60">→</span>
          <span className="rounded-md bg-white px-3 py-1.5 text-blue-600">
            Required&nbsp;
            <span className="font-semibold">{config.min_version}</span>
          </span>
        </div>
      </div>

      <div className="w-full max-w-sm">
        <Button
          onClick={openStore}
          className="h-14 w-full rounded-2xl bg-white text-base font-semibold text-blue-600 shadow-xl hover:bg-white/95"
        >
          <Download className="mr-2 h-5 w-5" />
          Update Now
        </Button>
        <p className="mt-4 text-center text-xs text-white/70">
          You will be redirected to the Play Store
        </p>
      </div>
    </div>
  );
}
